import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/lib/motion";
import { SERVICES } from "@/data/content";
import { SITE_URL } from "@/seo/site";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = SERVICES.find((item) => item.slug === slug);

  if (!service) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-40 text-center">
        <SEO title="Service not found | Risi Wellness Limited" description="The requested Risi Wellness service could not be found." pathname={`/services/${slug || ""}`} robots="noindex, follow" />
        <h1 className="font-serif text-4xl text-primary">Service not found</h1>
        <Link to="/services" className="inline-flex mt-8 rounded-full bg-primary text-primary-foreground px-7 py-3 font-semibold">View services</Link>
      </div>
    );
  }

  const title = `${service.name} in Manchester`;
  const description = `${service.description} Mobile appointments from Risi Wellness Limited.`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/services/${service.slug}#service`,
    name: service.name,
    serviceType: service.name,
    description,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: "Manchester and surrounding areas",
    url: `${SITE_URL}/services/${service.slug}`,
  };

  return (
    <div data-testid="service-detail-page">
      <SEO title={title} description={description} pathname={`/services/${service.slug}`} image={service.image} schema={schema} />
      <PageHero eyebrow={service.group} title={service.name} image={service.image} subtitle={service.tagline} />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <Link to="/services" className="inline-flex items-center gap-2 text-primary font-semibold mb-10"><ArrowLeft size={17} /> All services</Link>
        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-5xl text-primary mb-6">About this treatment</h2>
            <p className="text-muted-foreground text-lg leading-relaxed">{service.description}</p>
            <p className="mt-6 text-muted-foreground leading-relaxed">Each appointment is delivered as a mobile service for a home, hotel or workplace setting.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-border bg-card p-8 sm:p-10 space-y-8">
              <div>
                <h2 className="font-serif text-2xl text-primary mb-3">Treatment details</h2>
                <ul className="space-y-2">{service.benefits.map((benefit) => <li key={benefit} className="flex items-start gap-2 text-foreground/80"><Check size={17} className="text-primary mt-0.5 shrink-0" />{benefit}</li>)}</ul>
              </div>
              <div>
                <h2 className="font-serif text-2xl text-primary mb-3">Duration</h2>
                <ul className="space-y-1 text-muted-foreground">{service.durations.map((duration) => <li key={duration}>{duration}</li>)}</ul>
              </div>
              <div>
                <h2 className="font-serif text-2xl text-primary mb-3">Ideal for</h2>
                <p className="text-muted-foreground leading-relaxed">{service.idealFor}</p>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="mt-16 rounded-3xl bg-primary text-primary-foreground p-10 sm:p-14 text-center">
          <h2 className="font-serif text-3xl sm:text-5xl mb-4">Ready to choose a time?</h2>
          <p className="text-primary-foreground/80 mb-8">Use the Risi Wellness booking page to choose an available appointment.</p>
          <Link to={`/booking?service=${encodeURIComponent(service.name)}`} className="inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-8 py-4 font-semibold">Book this treatment <ArrowUpRight size={18} /></Link>
        </div>
      </main>
    </div>
  );
};

export default ServiceDetail;
